import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type ScenarioPoint = {
  name: string;
  value: number;
};

interface ScenarioAnalysisCardProps {
  data: ScenarioPoint[];
  colorA?: string;
  colorB?: string;
  colorC?: string;
  tickColor?: string;
  isShowTags?: boolean;
  emptyState?: React.ReactNode;
}

const getYAxisScale = (values: number[], minTicks: number = 5) => {
  const maxValue = Math.max(0, ...values);
  if (maxValue === 0) {
    return {
      max: 100,
      ticks: [0, 25, 50, 75, 100],
    };
  }

  const targetIntervals = Math.max(minTicks - 1, 1);
  const roughStep = maxValue / targetIntervals;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / magnitude;

  let step = 10 * magnitude;
  if (normalized <= 1) step = 1 * magnitude;
  else if (normalized <= 2) step = 2 * magnitude;
  else if (normalized <= 5) step = 5 * magnitude;

  const max = Math.ceil(maxValue / step) * step;
  const ticks = Array.from({ length: Math.floor(max / step) + 1 }, (_, i) => i * step);

  return { max, ticks };
};

export function ScenarioAnalysisCard({
  data,
  colorA = "#AD5E9D",
  colorB = "#3265AA",
  colorC = "#AD5E9D",
  tickColor = "#4c4c4d",
  isShowTags = true,
  emptyState,
}: ScenarioAnalysisCardProps) {
  const scenarioAxis = getYAxisScale(data.map((d) => d.value), 5);

  return (
    <div className="cma-card">
      <div className="cma-card-header">
        <div>
          <div className="cma-card-title">Scenario Analysis</div>
          <div className="cma-card-subtitle">Base / Optimistic / Pessimistic cost scenarios ($M)</div>
        </div>
        {isShowTags && <span className="cma-card-badge cma-card-badge-purple">SCENARIOS</span>}
      </div>

      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={data} barCategoryGap="20%">
            <XAxis dataKey="name" tick={{ fontSize: 9, fill: tickColor }} interval={0} angle={0} textAnchor="middle" height={44} />
            <YAxis domain={[0, scenarioAxis.max]} ticks={scenarioAxis.ticks} tick={{ fontSize: 12, fill: "#888888" }} />
            <Tooltip cursor={false} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
            <Bar dataKey="value" name="Cost ($K)" radius={[6, 6, 0, 0]} activeBar={false}>
              {data.map((_, i) => (
                <Cell key={i} fill={i % 3 === 0 ? colorA : i % 3 === 1 ? colorB : colorC} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        emptyState ?? <div className="cma-no-records">No Records Found</div>
      )}
    </div>
  );
}
